import { useState, useEffect, useMemo } from 'react';
import { api } from '@/services/api/client';
import { calculateDataStats, type DataStats } from '../utils/dataStatistics';
import { validateBillConsumption, type BillValidationResult } from '../utils/billValidation';

interface UseDataAlignmentProps {
  projectId?: string;
  originalData?: any;
  alignedData?: any;
  onDataAligned?: () => Promise<void>;
}

export function useDataAlignment({
  projectId,
  originalData,
  alignedData,
  onDataAligned
}: UseDataAlignmentProps) {
  // State for consumption
  const [consumptionMethod, setConsumptionMethod] = useState('seasonal');
  const [showOriginalConsumption, setShowOriginalConsumption] = useState(false);
  const [appliedConsumptionMethod, setAppliedConsumptionMethod] = useState<string | null>(null);

  // State for prices
  const [priceMethod, setPriceMethod] = useState('pattern');
  const [showOriginalPrice, setShowOriginalPrice] = useState(false);
  const [appliedPriceMethod, setAppliedPriceMethod] = useState<string | null>(null);

  // Force update mechanism
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // Extract responses from props
  const consumptionResponse = originalData?.consumption;
  const priceResponse = originalData?.prices;
  const alignedConsumptionResponse = alignedData?.consumption;
  const alignedPriceResponse = alignedData?.prices;
  const billResponse = originalData?.bill;

  // Transform data to expected format
  const consumption = consumptionResponse ? {
    data: consumptionResponse.data || consumptionResponse || [],
    totalRecords: consumptionResponse.totalRecords || consumptionResponse.data?.length || 0,
    avgConsumption: consumptionResponse.avgConsumption || 0,
    peakDemand: consumptionResponse.peakDemand || 0,
    loadFactor: consumptionResponse.loadFactor || 0
  } : null;

  const prices = priceResponse ? {
    data: priceResponse.data || priceResponse || [],
    records: priceResponse.records || priceResponse.totalRecords || priceResponse.data?.length || 0,
    average: priceResponse.average || 0,
    peak: priceResponse.peak || 0,
    valley: priceResponse.valley || 0
  } : null;

  // Update applied methods when data changes
  useEffect(() => {
    if (alignedConsumptionResponse?.completionMethod) {
      setAppliedConsumptionMethod(alignedConsumptionResponse.completionMethod);
    }
  }, [alignedConsumptionResponse]);

  useEffect(() => {
    if (alignedPriceResponse?.completionMethod) {
      setAppliedPriceMethod(alignedPriceResponse.completionMethod);
    }
  }, [alignedPriceResponse]);

  // Determine whether data is aligned
  const isConsumptionAligned = useMemo(
    () => alignedConsumptionResponse?.is_aligned === true,
    [alignedConsumptionResponse, updateTrigger]
  );

  const isPriceAligned = useMemo(
    () => alignedPriceResponse?.is_aligned === true,
    [alignedPriceResponse, updateTrigger]
  );

  // Use aligned data if available
  const alignedConsumption = isConsumptionAligned ? alignedConsumptionResponse : null;
  const alignedPrices = isPriceAligned ? alignedPriceResponse : null;

  // Helper to select data source based on view preference
  function selectDataSource<T>(showOriginal: boolean, original: T | null, aligned: T | null): T | null {
    if (showOriginal) return original;
    return aligned ?? original;
  }

  // Calculate statistics - use aligned data if available and not showing original
  const consumptionDataForStats = selectDataSource(showOriginalConsumption, consumption, alignedConsumption);

  const consumptionStats: DataStats = useMemo(
    () => calculateDataStats(consumptionDataForStats?.data || [], 'consumption'),
    [consumptionDataForStats]
  );

  // Extract and calculate demand stats
  const demandData = consumptionDataForStats ? {
    data: consumptionDataForStats.data || [],
    hasDemandData: consumptionDataForStats.hasDemandData || false,
    isDemandEstimated: consumptionDataForStats.isDemandEstimated || false,
    avgDemand: consumptionDataForStats.avgDemand || 0,
    peakDemand: consumptionDataForStats.peakDemand || 0,
    minDemand: consumptionDataForStats.minDemand || 0
  } : null;

  const demandStats: (DataStats & { peakDemand?: number; avgDemand?: number; minDemand?: number; loadFactor?: number }) | null = useMemo(() => {
    if (!demandData?.hasDemandData) return null;

    const stats = calculateDataStats(
      demandData.data || [],
      'power_kw',
      'power'
    );

    // Add peak, avg, and load factor to demandStats
    stats.peakDemand = demandData.peakDemand || 0;
    stats.avgDemand = demandData.avgDemand || 0;
    stats.minDemand = demandData.minDemand || 0;
    stats.loadFactor = stats.avgDemand > 0 && stats.peakDemand > 0
      ? (stats.avgDemand / stats.peakDemand * 100)
      : 0;

    return stats;
  }, [demandData]);

  const priceDataForStats = selectDataSource(showOriginalPrice, prices, alignedPrices);

  const priceStats: DataStats = useMemo(
    () => calculateDataStats(priceDataForStats?.data || [], 'priceUsd'),
    [priceDataForStats]
  );

  // Calculate bill validation
  const billValidation: BillValidationResult | null = useMemo(
    () => validateBillConsumption(consumptionDataForStats, billResponse),
    [consumptionDataForStats, billResponse]
  );

  // Handle data alignment
  const handleAlignData = async (
    dataType: 'consumption' | 'price',
    method: string,
    shiftMonths?: number,
    duplicateToYear?: boolean,
    normalizeToYear?: boolean
  ) => {
    try {
      const data = dataType === 'consumption' ? consumption : prices;

      if (!data || !data.data) {
        console.error(`No ${dataType} data to align`);
        return;
      }

      // Use V2 API - backend reads from database
      await api.post('/data/complete/v2', {
        data_type: dataType,
        method,
        project_id: projectId,
        shift_months: shiftMonths || 0,
        duplicate_to_year: duplicateToYear || false,
        normalize_to_year: normalizeToYear || false
      });

      if (dataType === 'consumption') {
        setAppliedConsumptionMethod(method);
      } else {
        setAppliedPriceMethod(method);
      }

      // Refetch all project data to get updated aligned data
      if (onDataAligned) {
        await onDataAligned();
      }

      // Force component re-render to pick up new data
      setUpdateTrigger(prev => prev + 1);

    } catch (error) {
      console.error(`Error aligning ${dataType} data:`, error);
    }
  };

  const handleRevertData = (dataType: 'consumption' | 'price') => {
    if (dataType === 'consumption') {
      setShowOriginalConsumption(false);
      setAppliedConsumptionMethod(null);
    } else {
      setShowOriginalPrice(false);
      setAppliedPriceMethod(null);
    }
  };

  const toggleOriginalView = (dataType: 'consumption' | 'price') => {
    if (dataType === 'consumption') {
      setShowOriginalConsumption(!showOriginalConsumption);
    } else {
      setShowOriginalPrice(!showOriginalPrice);
    }
  };

  // Data for validation - use aligned data if available
  const validationConsumptionData = alignedConsumption || consumption;
  const validationPriceData = alignedPrices || prices;

  return {
    // Consumption state
    consumption: consumptionDataForStats,
    consumptionStats,
    isConsumptionAligned,
    consumptionMethod,
    appliedConsumptionMethod,
    showOriginalConsumption,

    // Demand data
    demandData,
    demandStats,

    // Price state
    prices: priceDataForStats,
    priceStats,
    isPriceAligned,
    priceMethod,
    appliedPriceMethod,
    showOriginalPrice,

    // Bill validation
    billValidation,

    // Validation data
    validationConsumptionData,
    validationPriceData,

    // Actions
    setConsumptionMethod,
    setPriceMethod,
    handleAlignData,
    handleRevertData,
    toggleOriginalView
  };
}
